import type { ClockParts, DaySchedule, ScenarioId, ScheduleResult } from "./types";

const weekdayMap: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

export function getClockParts(date: Date, timezone: string): ClockParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric", month: "2-digit", day: "2-digit", weekday: "short",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "0";
  return {
    year: Number(value("year")), month: Number(value("month")), dayOfMonth: Number(value("day")),
    dayOfWeek: weekdayMap[value("weekday")] ?? 1,
    hour: Number(value("hour")), minute: Number(value("minute")), second: Number(value("second")),
  };
}

export function toMinutes(value: string): number {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) throw new Error(`Некорректное время: ${value}`);
  const [hour = "0", minute = "0"] = value.split(":");
  return Number(hour) * 60 + Number(minute);
}

function zonedDate(parts: Pick<ClockParts, "year" | "month" | "dayOfMonth">, time: string, timezone: string): Date {
  const [hour = "0", minute = "0"] = time.split(":");
  const intended = Date.UTC(parts.year, parts.month - 1, parts.dayOfMonth, Number(hour), Number(minute));
  let candidate = new Date(intended);
  for (let index = 0; index < 3; index += 1) {
    const actual = getClockParts(candidate, timezone);
    const actualAsUtc = Date.UTC(actual.year, actual.month - 1, actual.dayOfMonth, actual.hour, actual.minute, actual.second);
    candidate = new Date(candidate.getTime() + intended - actualAsUtc);
  }
  return candidate;
}

function addCalendarDays(parts: ClockParts, amount: number): Pick<ClockParts, "year" | "month" | "dayOfMonth"> {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.dayOfMonth + amount, 12));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, dayOfMonth: date.getUTCDate() };
}

export function validateSchedule(schedule: DaySchedule[]): void {
  if (schedule.length !== 7 || new Set(schedule.map((day) => day.day)).size !== 7) throw new Error("Расписание должно содержать семь уникальных дней");
  for (const day of schedule) {
    const points = [day.opensAt, day.morningUntil, day.daytimeUntil, day.closesAt].map(toMinutes);
    if (!(points[0]! < points[1]! && points[1]! < points[2]! && points[2]! < points[3]!)) throw new Error(`Некорректные границы расписания для дня ${day.day}`);
  }
}

export function getScheduleState(date: Date, timezone: string, schedule: DaySchedule[]): ScheduleResult {
  validateSchedule(schedule);
  const clock = getClockParts(date, timezone);
  const day = schedule.find((item) => item.day === clock.dayOfWeek)!;
  const nowMinutes = clock.hour * 60 + clock.minute;
  const opening = toMinutes(day.opensAt);
  const morningEnd = toMinutes(day.morningUntil);
  const daytimeEnd = toMinutes(day.daytimeUntil);
  const closing = toMinutes(day.closesAt);
  let scenario: ScenarioId;
  let nextTime: string;
  let nextOpeningLabel: string | undefined;

  if (nowMinutes < opening) {
    scenario = "CLOSED";
    nextTime = day.opensAt;
    nextOpeningLabel = `Ждём вас сегодня с ${day.opensAt}`;
  } else if (nowMinutes < morningEnd) {
    scenario = "MORNING";
    nextTime = day.morningUntil;
  } else if (nowMinutes < daytimeEnd) {
    scenario = day.daytimeScenario;
    nextTime = day.daytimeUntil;
  } else if (nowMinutes < closing) {
    scenario = "EVENING";
    nextTime = day.closesAt;
  } else {
    scenario = "CLOSED";
    const tomorrowDay = clock.dayOfWeek === 7 ? 1 : clock.dayOfWeek + 1;
    const tomorrow = schedule.find((item) => item.day === tomorrowDay)!;
    nextTime = tomorrow.opensAt;
    nextOpeningLabel = `Ждём вас завтра с ${tomorrow.opensAt}`;
    const target = addCalendarDays(clock, 1);
    return { scenario, clock, nextBoundaryAt: zonedDate(target, nextTime, timezone), nextOpeningLabel };
  }
  return { scenario, clock, nextBoundaryAt: zonedDate(clock, nextTime, timezone), nextOpeningLabel };
}
